import { Inject, Injectable, Injector } from '@angular/core';

import type { MessagePayload, Messaging } from 'firebase/messaging';
import { deleteToken, getToken, isSupported, onMessage } from 'firebase/messaging';

import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  concat,
  debounceTime,
  defaultIfEmpty,
  filter,
  from,
  map,
  mergeMap,
  observeOn,
  of,
  shareReplay,
  subscribeOn,
  switchMap,
} from 'rxjs';

import { AuthenticationSessionService } from '@finverity/authentication';
import { CreatePushTokenGQL, PushTokenType } from '@finverity/graphql';

import { FIREBASE_MESSAGING } from './firebase-messaging.provider';
import { FvFirebaseZoneSchedulers } from './firebase-zone-schedulers';
import { NOTIFICATION, PERMISSIONS, SERVICE_WORKER_CONTAINER } from './web-api-tokens';

/**
 * @link https://firebase.google.com/docs/cloud-messaging/js/receive#web-version-9_3
 * @warnings https://github.com/firebase/firebase-js-sdk/issues/2364 - status open
 * */
@Injectable({
  providedIn: 'root',
})
export class FirebaseCloudMessagingService {
  readonly requestPermission: Observable<NotificationPermission>;
  readonly getToken: Observable<string | null>;
  readonly tokenChanges: Observable<string | null>;
  readonly messages: Observable<MessagePayload>;
  readonly requestToken: Observable<string | null>;
  readonly deleteToken: (token: string) => Observable<boolean>;

  private setupPushTokenSubscription: Subscription = Subscription.EMPTY;

  constructor(
    @Inject(SERVICE_WORKER_CONTAINER) private readonly serviceWorker: ServiceWorkerContainer,
    @Inject(NOTIFICATION) private readonly notification: typeof Notification,
    @Inject(PERMISSIONS) private readonly permissions: Permissions,
    private readonly injector: Injector,
    readonly schedulers: FvFirebaseZoneSchedulers,
    private readonly authenticationSessionService: AuthenticationSessionService,
    private readonly createPushTokenGQL: CreatePushTokenGQL
  ) {
    const messaging = of(undefined).pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(() => isSupported() as Promise<boolean>),
      map(supported => (supported ? this.getMessagingInstance() : null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.requestPermission = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(() => this.notification.requestPermission())
    );

    this.getToken = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(messaging => {
        if (this.notification.permission === 'granted') {
          return this.registerServiceWorker().pipe(
            switchMap(serviceWorkerRegistration => getToken(messaging!, { serviceWorkerRegistration }))
          );
        }

        return of(null);
      })
    );

    const notificationPermission$ = this.createNotificationPermissionsStatusChangeObservable();

    const tokenChange$ = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(() => notificationPermission$),
      switchMap(() => this.getToken)
    );

    this.tokenChanges = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(() => concat(this.getToken, tokenChange$))
    );

    this.messages = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(messaging => this.createMessagesObservable(messaging!))
    );

    this.requestToken = messaging.pipe(
      // @ts-ignore
      subscribeOn(schedulers.outsideAngular),
      // @ts-ignore
      observeOn(schedulers.insideAngular),
      switchMap(() => this.requestPermission),
      catchError(() => of(null)),
      mergeMap(() => this.tokenChanges)
    );

    this.deleteToken = () =>
      messaging.pipe(
        // @ts-ignore
        subscribeOn(schedulers.outsideAngular),
        // @ts-ignore
        observeOn(schedulers.insideAngular),
        switchMap(messaging => deleteToken(messaging!)),
        defaultIfEmpty(false)
      );
  }

  init(): void {
    this.requestPermission.subscribe();

    this.setupPushTokenSubscription = this.authenticationSessionService.sessionRevision$
      .pipe(
        debounceTime(0), // One registration for the session changes made within the same task
        filter(() => this.authenticationSessionService.hasSession()),
        switchMap(() =>
          this.requestToken.pipe(switchMap(fcmToken => (fcmToken ? this.setupPushToken(fcmToken) : EMPTY)))
        )
      )
      .subscribe();
  }

  // Lookup Messaging instance through Injector only when isSupported() check has been done
  getMessagingInstance(): Messaging {
    return this.injector.get(FIREBASE_MESSAGING);
  }

  private setupPushToken(token: string): Observable<boolean> {
    return this.createPushTokenGQL
      .mutate({ variables: { createPushTokenInput: { type: PushTokenType.Web, token } } })
      .pipe(
        map(result => !!result.data?.createPushToken),
        catchError(() => EMPTY)
      );
  }

  private createNotificationPermissionsStatusChangeObservable(): Observable<PermissionState> {
    return new Observable<PermissionState>(subscriber => {
      this.permissions.query({ name: 'notifications' }).then(permissionStatus => {
        permissionStatus.onchange = event => subscriber.next((event.target as PermissionStatus).state);
      });
    });
  }

  private createMessagesObservable(messaging: Messaging): Observable<MessagePayload> {
    return new Observable<MessagePayload>(subscriber => {
      return onMessage(messaging, messagePayload => {
        subscriber.next(messagePayload);
      });
    });
  }

  // type: 'module' only supported in the browsers Chrome and Safari, not in Mozilla
  // because of that serviceWorker registration file(firebase-messaging-sw.js) using importScripts for -compat libraries
  // https://github.com/firebase/firebase-js-sdk/issues/5403
  private registerServiceWorker(): Observable<ServiceWorkerRegistration> {
    return from(
      this.serviceWorker
        .register('service-workers/firebase-messaging-sw.js', { type: 'classic' })
        .then(serviceWorkerRegistration => serviceWorkerRegistration)
        .catch(error => {
          console.warn({ serviceWorkerRegistrationFailure: error });
          throw error;
        })
    );
  }
}
